import "@/assets/vendor/css/reset.css"; // TODO: this is stupid
import "@/assets/reset.css";
import "office-ui-fabric-core/dist/css/fabric.min.css";

import {
  createTheme,
  initializeIcons,
  loadTheme,
  registerIcons
} from "@fluentui/react";
import clarity from "@microsoft/clarity";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { ApiProvider } from "./core/libs/api/react-query/useApi";
import { AlertProvider } from "./hooks/useAlert";
import { ApiAlertsProvider } from "./hooks/useApiAlerts";
import { ConfirmModalProvider } from "./hooks/useConfirmModal";
import { NotificationsProvider } from "./hooks/useNotifications";
import { OrganizationProvider } from "./hooks/useOrganization";
import { SelectedBuProvider } from "./hooks/useSelectedBu";

import { SandboxBarMessage } from "@/components/Shared/SandboxBarMessage";
import { AuthProvider } from "@/core/libs/use-auth";
import Routes from "@/core/routes/Routes";
import store from "@/core/store/store";

const libertasTheme = createTheme({
  palette: {
    themePrimary: "#395ca3",
    themeLighterAlt: "#f5f7fb",
    themeLighter: "#d7e0f0",
    themeLight: "#b7c6e3",
    themeTertiary: "#7a94c8",
    themeSecondary: "#4b6cae",
    themeDarkAlt: "#335393",
    themeDark: "#2b467c",
    themeDarker: "#20345b",
    neutralLighterAlt: "#faf9f8",
    neutralLighter: "#f3f2f1",
    neutralLight: "#edebe9",
    neutralQuaternaryAlt: "#e1dfdd",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c6c4",
    neutralTertiary: "#a19f9d",
    neutralSecondary: "#605e5c",
    neutralPrimaryAlt: "#3b3a39",
    neutralPrimary: "#323130",
    neutralDark: "#201f1e",
    black: "#000000",
    white: "#ffffff",
    orangeLight: "#ff5714",
    purple: "#532cee",
    greenLight: "#00a3a5"
  },
  effects: {
    elevation4:
      "0 1.6px 3.6px 0 rgba(0, 0, 0, 0.132), 0 0.3px 0.9px 0 rgba(0, 0, 0, 0.108)",
    roundedCorner4: "1rem",
    roundedCorner2: "0.35rem"
  }
});

const queryClient = new QueryClient();

const projectId = import.meta.env.VITE_CLARITY_CLIENT_ID;

function App(): JSX.Element {
  loadTheme(libertasTheme);
  initializeIcons();
  registerIcons({
    icons: {
      libertas: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="80.469"
          height="18"
          viewBox="0 0 114.267 25.56"
        >
          <g id="Logo" transform="translate(0 2)">
            <path
              id="Path_185"
              data-name="Path 185"
              d="M1.638-14.868H4.914V-3.024h6.069V0H1.638Zm11.529,0h3.276V0H13.167Zm6.678,0h5.544a14.016,14.016,0,0,1,1.943.137,5.306,5.306,0,0,1,1.753.546,3.358,3.358,0,0,1,1.26,1.144,3.508,3.508,0,0,1,.483,1.953,3.118,3.118,0,0,1-.7,2.09,3.868,3.868,0,0,1-1.859,1.186v.042a4.242,4.242,0,0,1,1.333.41,3.562,3.562,0,0,1,1.029.777,3.31,3.31,0,0,1,.662,1.1,3.873,3.873,0,0,1,.231,1.344,3.574,3.574,0,0,1-.5,1.964A3.962,3.962,0,0,1,29.715-.9a5.71,5.71,0,0,1-1.8.693A9.611,9.611,0,0,1,25.935,0h-6.09Zm3.276,5.943h2.373a3.335,3.335,0,0,0,.746-.084,2.062,2.062,0,0,0,.662-.273,1.421,1.421,0,0,0,.472-.5,1.51,1.51,0,0,0,.178-.756,1.371,1.371,0,0,0-.2-.767,1.388,1.388,0,0,0-.514-.472,2.452,2.452,0,0,0-.714-.241,4.285,4.285,0,0,0-.777-.073H23.121Zm0,6.153h2.94a3.62,3.62,0,0,0,.766-.084,2.028,2.028,0,0,0,.7-.294,1.672,1.672,0,0,0,.515-.546,1.578,1.578,0,0,0,.2-.819,1.313,1.313,0,0,0-.262-.851,1.664,1.664,0,0,0-.662-.494,3.388,3.388,0,0,0-.861-.231,6.4,6.4,0,0,0-.861-.063H23.121ZM34-14.868H44.1v3.024H37.275v2.772h6.447v3.024H37.275v3.024h7.2V0H34Zm13.524,0h5.754a10.153,10.153,0,0,1,2.153.22,5.107,5.107,0,0,1,1.785.735,3.662,3.662,0,0,1,1.218,1.386,4.694,4.694,0,0,1,.451,2.174,4.364,4.364,0,0,1-.819,2.677,3.687,3.687,0,0,1-2.394,1.4L59.451,0H55.524L52.416-5.943H50.8V0H47.523ZM50.8-8.715h1.932q.441,0,.934-.031a2.927,2.927,0,0,0,.893-.189,1.544,1.544,0,0,0,.661-.493,1.5,1.5,0,0,0,.263-.945,1.569,1.569,0,0,0-.231-.9,1.54,1.54,0,0,0-.588-.514,2.608,2.608,0,0,0-.819-.241,6.68,6.68,0,0,0-.9-.063H50.8ZM64.05-11.97H59.808v-2.9h11.76v2.9H67.326V0H64.05Zm49.587.735a2.333,2.333,0,0,0-1.081-.745,3.876,3.876,0,0,0-1.292-.241,3.384,3.384,0,0,0-.735.084,2.817,2.817,0,0,0-.714.262,1.556,1.556,0,0,0-.546.473,1.2,1.2,0,0,0-.21.714,1.184,1.184,0,0,0,.5,1.029,4.773,4.773,0,0,0,1.271.609q.766.252,1.648.5a6.793,6.793,0,0,1,1.648.714A3.9,3.9,0,0,1,115.4-6.594a3.776,3.776,0,0,1,.5,2.079,4.78,4.78,0,0,1-.462,2.163,4.389,4.389,0,0,1-1.249,1.533,5.317,5.317,0,0,1-1.827.9,8.027,8.027,0,0,1-2.194.294,7.961,7.961,0,0,1-2.688-.441,6.736,6.736,0,0,1-2.31-1.428l2.331-2.562a3.4,3.4,0,0,0,1.271,1.039,3.627,3.627,0,0,0,1.585.367,3.587,3.587,0,0,0,.809-.094,2.634,2.634,0,0,0,.735-.283,1.585,1.585,0,0,0,.525-.483,1.2,1.2,0,0,0,.2-.693,1.257,1.257,0,0,0-.514-1.06,4.742,4.742,0,0,0-1.292-.661q-.777-.273-1.68-.546a7.544,7.544,0,0,1-1.68-.735,4.049,4.049,0,0,1-1.292-1.218,3.485,3.485,0,0,1-.515-1.995,4.466,4.466,0,0,1,.473-2.1,4.556,4.556,0,0,1,1.26-1.512,5.478,5.478,0,0,1,1.816-.913,7.435,7.435,0,0,1,2.121-.3,8.34,8.34,0,0,1,2.436.357,5.618,5.618,0,0,1,2.121,1.2Z"
              transform="translate(-1.638 18.88)"
              fill="#2a4987"
            />
            <path
              id="Path_186"
              data-name="Path 186"
              d="M16-22.88,2.976-.32H29.024Zm0,4.1L25.568-2.336H6.432Z"
              transform="translate(70.362 22.88)"
              fill="#b71c1c"
              stroke="#b71c1c"
              strokeWidth="2"
            />
          </g>
        </svg>
      )
    }
  });

  useEffect(() => {
    if (projectId) {
      clarity.init(projectId);
    }
  }, []);

  return (
    <div className="ms-Fabric" style={{ height: "100vh", overflow: "hidden" }}>
      <AuthProvider>
        <ApiAlertsProvider>
          <AlertProvider>
            <ApiProvider>
              <QueryClientProvider client={queryClient}>
                <NotificationsProvider>
                  <SelectedBuProvider>
                    <ConfirmModalProvider>
                      <OrganizationProvider>
                        <Provider store={store}>
                          <BrowserRouter>
                            {import.meta.env.VITE_IS_SANDBOX === "true" && (
                              <SandboxBarMessage />
                            )}
                            <Routes />
                          </BrowserRouter>
                        </Provider>
                      </OrganizationProvider>
                    </ConfirmModalProvider>
                  </SelectedBuProvider>
                </NotificationsProvider>
              </QueryClientProvider>
            </ApiProvider>
          </AlertProvider>
        </ApiAlertsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
